import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, EmptyState, Modal, TemporaryPasswordPanel } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import {
  useCreateStaffAccountMutation,
  useInviteStaffMutation,
  useReissueStaffPasswordMutation,
  useStaffQuery,
} from '../api';
import { InviteStaffForm } from '../components/InviteStaffForm';
import { ReissuePasswordForm } from '../components/ReissuePasswordForm';
import { StaffTable } from '../components/StaffTable';
import type { InviteStaffFormValues } from '../schemas';
import type { StaffMember } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { useActiveShop } from '@/features/shops/use-active-shop';

/**
 * What is on screen in the single modal: nothing, one of the three forms, or - after a success - the provisional password.
 *
 * The password lives HERE, in component state, and nowhere else: the mutations are collected at once and reset the moment they succeed,
 * and `issued` is dropped when the person says they noted it, when the display window ends, or when the modal closes.
 */
type Dialog =
  | { readonly kind: 'closed' }
  | { readonly kind: 'invite' }
  | { readonly kind: 'create' }
  | { readonly kind: 'reissue'; readonly member: StaffMember }
  | {
      readonly kind: 'issued';
      readonly account: string;
      readonly password: string;
      readonly expiresAt: string;
    };

export function StaffPage(): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const staffQuery = useStaffQuery();
  const inviteMutation = useInviteStaffMutation();
  const createMutation = useCreateStaffAccountMutation();
  const reissueMutation = useReissueStaffPasswordMutation();
  const shop = useActiveShop();
  const [dialog, setDialog] = useState<Dialog>({ kind: 'closed' });
  const [formError, setFormError] = useState<string | null>(null);

  const isOwner = shop.activeMembership?.roleKey === 'merchant_owner';

  function open(next: Dialog): void {
    setFormError(null);
    setDialog(next);
  }

  /** Closing forgets the password, whatever the way out. */
  function close(): void {
    createMutation.reset();
    reissueMutation.reset();
    setDialog({ kind: 'closed' });
  }

  async function onInvite(values: InviteStaffFormValues): Promise<void> {
    setFormError(null);
    try {
      await inviteMutation.mutateAsync(values);
      toast.success(t('merchant.staff.inviteSuccess'));
      close();
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  async function onCreate(values: InviteStaffFormValues): Promise<void> {
    setFormError(null);
    try {
      const created = await createMutation.mutateAsync(values);
      // Copied into state, then the mutation (and the password inside it) is dropped at once.
      setDialog({
        kind: 'issued',
        account: values.email,
        password: created.temporaryPassword,
        expiresAt: created.expiresAt,
      });
      createMutation.reset();
    } catch (error) {
      createMutation.reset();
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  async function onReissue(member: StaffMember, reason: string): Promise<void> {
    setFormError(null);
    try {
      const issued = await reissueMutation.mutateAsync({ membershipId: member.id, reason });
      setDialog({
        kind: 'issued',
        account: member.email,
        password: issued.temporaryPassword,
        expiresAt: issued.expiresAt,
      });
      reissueMutation.reset();
    } catch (error) {
      reissueMutation.reset();
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  if (!isOwner && !shop.isLoading) {
    return (
      <EmptyState
        title={t('merchant.staff.ownerOnlyTitle')}
        description={t('merchant.staff.ownerOnlyDescription')}
      />
    );
  }

  const modalTitle =
    dialog.kind === 'invite'
      ? t('merchant.staff.inviteMember')
      : dialog.kind === 'create'
        ? t('merchant.staff.createAccountTitle')
        : dialog.kind === 'reissue'
          ? t('merchant.staff.reissueTitle')
          : dialog.kind === 'issued'
            ? t('merchant.staff.accountCreatedTitle')
            : '';

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">{t('merchant.nav.team')}</h1>
          <p className="text-sm text-inkFaint">{t('merchant.staff.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => {
              open({ kind: 'create' });
            }}
          >
            {t('merchant.staff.createAccount')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              open({ kind: 'invite' });
            }}
          >
            {t('merchant.staff.inviteMember')}
          </Button>
        </div>
      </div>

      {staffQuery.isPending || shop.isLoading ? (
        <TableSkeleton columns={5} />
      ) : staffQuery.isError ? (
        <EmptyState
          title={t('merchant.staff.loadError')}
          description={t(resolveErrorMessageKey(staffQuery.error))}
        />
      ) : staffQuery.data.length === 0 ? (
        <EmptyState title={t('merchant.staff.emptyTitle')} />
      ) : (
        <StaffTable
          members={staffQuery.data}
          onReissue={(member) => {
            open({ kind: 'reissue', member });
          }}
        />
      )}

      <Modal
        open={dialog.kind !== 'closed'}
        title={modalTitle}
        onClose={close}
        footer={
          dialog.kind === 'invite' ? (
            <>
              <Button variant="secondary" onClick={close}>
                {t('merchant.common.cancel')}
              </Button>
              <Button type="submit" form="invite-staff-form" loading={inviteMutation.isPending}>
                {t('merchant.staff.inviteAction')}
              </Button>
            </>
          ) : dialog.kind === 'create' ? (
            <>
              <Button variant="secondary" onClick={close}>
                {t('merchant.common.cancel')}
              </Button>
              <Button
                type="submit"
                form="create-staff-account-form"
                loading={createMutation.isPending}
              >
                {t('merchant.staff.createAccountSubmit')}
              </Button>
            </>
          ) : dialog.kind === 'reissue' ? (
            <>
              <Button variant="secondary" onClick={close}>
                {t('merchant.common.cancel')}
              </Button>
              <Button
                type="submit"
                form="reissue-password-form"
                loading={reissueMutation.isPending}
              >
                {t('merchant.staff.reissueSubmit')}
              </Button>
            </>
          ) : undefined
        }
      >
        {formError !== null && dialog.kind !== 'issued' ? (
          <p
            role="alert"
            className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical"
          >
            {formError}
          </p>
        ) : null}

        {dialog.kind === 'invite' ? (
          <InviteStaffForm
            onSubmit={(values) => {
              void onInvite(values);
            }}
            isSubmitting={inviteMutation.isPending}
          />
        ) : null}

        {dialog.kind === 'create' ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-inkSoft">{t('merchant.staff.createAccountIntro')}</p>
            <InviteStaffForm
              formId="create-staff-account-form"
              onSubmit={(values) => {
                void onCreate(values);
              }}
              isSubmitting={createMutation.isPending}
            />
          </div>
        ) : null}

        {dialog.kind === 'reissue' ? (
          <ReissuePasswordForm
            formId="reissue-password-form"
            disabled={reissueMutation.isPending}
            onSubmit={(reason) => {
              void onReissue(dialog.member, reason);
            }}
          />
        ) : null}

        {dialog.kind === 'issued' ? (
          <TemporaryPasswordPanel
            account={dialog.account}
            password={dialog.password}
            expiresAt={dialog.expiresAt}
            onDone={close}
            onExpire={() => {
              setDialog({
                kind: 'issued',
                account: dialog.account,
                password: '',
                expiresAt: dialog.expiresAt,
              });
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
}
